import { MouseEvent, useContext } from 'react';
import { ClickAwayListener, Menu as MuiMenu, Slide, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'storage/store';
import { menuClickEvent } from 'utils/events';
import sendEvent from 'utils/sendEvent';
import { IsLoggedIn } from 'utils/user';
import { Icon } from 'components/Icon';
import { useContractOrAgreement } from 'hooks/useContractOrAgreement';
import { getMenuList } from '../utils';
import { DesktopMenuAnchorContext } from '../utils/desktopMenuAnchorContext';
import { LinkStyled } from '../styled/LinkStyled';
import { NavLinkStyled } from '../styled/NavLinkStyled';
import { UserInfo } from '../styled/UserInfo';
import MenuItem from '../styled/MenuItem';
import AppBarButtons from './AppBarButtons';
import BadgeAndMenu from './Notifications/BadgeAndMenu';

interface IDesktopMenu {
  username: string;
}

const DesktopMenu = ({ username }: IDesktopMenu) => {
  const { t } = useTranslation();
  const { anchorElNav, setAnchorEl } = useContext(DesktopMenuAnchorContext);
  const contractsToggled = useSelector(
    (state: RootState) => state.application?.contractDashboardFeatureToggle
  );
  const { agreements } = useContractOrAgreement();
  const isUserProfileAvailable =
    !contractsToggled || Boolean(agreements?.length ?? 0);

  const menuList = getMenuList(t, isUserProfileAvailable);

  const primaryMenuAction = (text: string) => {
    sendEvent({
      labelText: text,
      ...menuClickEvent
    });
  };

  if (!setAnchorEl) return null;

  const secondaryMenuAction = (text: string) => {
    setAnchorEl(null);
    sendEvent({
      labelText: text,
      ...menuClickEvent
    });
  };

  return (
    <>
      <Grid
        container
        sx={{
          alignItems: 'center',
          flex: '1',
          marginLeft: '68px'
        }}
      >
        {menuList.primary.items
          .filter((item) => (IsLoggedIn() ? true : !item.loggedOnly))
          .map((item) => (
            <Grid
              key={item.text}
              sx={{
                fontWeight: 700,
                marginRight: '69px'
              }}
            >
              <NavLinkStyled
                className={({ isActive }) => (isActive ? 'active' : '')}
                end
                $color="primary"
                to={item.href}
                onClick={() => primaryMenuAction(item.text)}
              >
                {item.text}
              </NavLinkStyled>
            </Grid>
          ))}
      </Grid>
      {IsLoggedIn() ? (
        <Grid
          sx={{
            pr: 5
          }}
        >
          <BadgeAndMenu />
        </Grid>
      ) : null}
      <Grid
        sx={{
          display: 'flex'
        }}
      >
        {username && (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Grid
              container
              onClick={(event: MouseEvent<HTMLElement>) =>
                setAnchorEl(anchorElNav ? null : event.currentTarget)
              }
              sx={{
                alignItems: 'center',
                marginRight: '40px',
                cursor: 'pointer'
              }}
            >
              <UserInfo
                variant="body2"
                sx={{ marginRight: '10px', color: 'secondary.main' }}
              >
                <Icon icon="user" color="secondary" />
                {username}
              </UserInfo>
              <Icon icon="chevronDown" fontSize="12px" color="secondary" />
            </Grid>
          </ClickAwayListener>
        )}
        <MuiMenu
          anchorEl={anchorElNav}
          elevation={2}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          open={Boolean(anchorElNav)}
          onClose={() => setAnchorEl(null)}
          sx={{
            display: { xs: 'none', lg: 'block' },
            padding: '10px',
            marginTop: '22px',
            zIndex: 1300
          }}
          slots={{ transition: Slide }}
          disableAutoFocusItem
          disableScrollLock
        >
          {menuList.secondary.items
            .filter((item) => item.isVisible)
            .map((item) => (
              <MenuItem
                onClick={() => secondaryMenuAction(item.text)}
                key={item.text}
              >
                <LinkStyled to={item.href}>{item.text}</LinkStyled>
              </MenuItem>
            ))}
        </MuiMenu>
        <Grid>
          <AppBarButtons forDesktop />
        </Grid>
      </Grid>
    </>
  );
};

export default DesktopMenu;
